import { getSelectedKeyboardLayoutConfig } from "../../../lib/utils";
import {
  escapeHtml,
  mountHQPanel,
  removeHQPanel,
  renderTextWithHighlights,
  resolveKeyForLayout,
} from "./hqShared.js";

const ORANGE = "#DE5700";
const TEXT_COLOR = "#0A0A0A";
const HINT_COLOR = "#828282";
const CARD_BG = "#FFFFFF";

function autoFitTextToContainer(textEl, containerEl, minFontSizePx) {
  if (!textEl || !containerEl) return;

  const computed = window.getComputedStyle(textEl);
  let fontSize = Number.parseFloat(computed.fontSize);
  if (!Number.isFinite(fontSize)) return;

  const minSize = Number.isFinite(minFontSizePx) ? minFontSizePx : 12;

  while (
    fontSize > minSize
    && (textEl.scrollWidth > containerEl.clientWidth || textEl.scrollHeight > containerEl.clientHeight)
  ) {
    fontSize -= 1;
    textEl.style.fontSize = `${fontSize}px`;
  }
}

function fitHQMessageText(wrapper, config = {}) {
  const mainTextEl = wrapper.querySelector('[data-hq-main-text="true"]');
  const mainTextContainerEl = wrapper.querySelector('[data-hq-main-container="true"]');
  const continueTextEl = wrapper.querySelector('[data-hq-continue-text="true"]');

  const minMainFontSizePx = Number.isFinite(config.minMainFontSizePx)
    ? config.minMainFontSizePx
    : 11;

  autoFitTextToContainer(mainTextEl, mainTextContainerEl, minMainFontSizePx);

  if (!mainTextEl || !continueTextEl) return;

  let fontSize = Number.parseFloat(window.getComputedStyle(mainTextEl).fontSize);
  if (!Number.isFinite(fontSize)) return;

  // Extra safety: keep shrinking until the main text no longer collides with the continue hint.
  while (fontSize > minMainFontSizePx) {
    const mainRect = mainTextEl.getBoundingClientRect();
    const continueRect = continueTextEl.getBoundingClientRect();
    const overlaps = mainRect.bottom > continueRect.top;
    if (!overlaps) break;

    fontSize -= 1;
    mainTextEl.style.fontSize = `${fontSize}px`;
  }
}

/**
 * Builds the HTML string for the HQ message panel.
 *
 * @param {JsPsych} jsPsych
 * @param {Object} config
 * @param {string}  [config.portrait="hq-impressed"]  - Filename (no extension) inside assets/images/instructions/custom/
 * @param {string}   config.text                       - Main message. Use {{KeyCode}} for dynamic keys.
 * @param {Array}   [config.highlights=[]]             - Highlight entries: { text } or { conditional: { key } }
 * @param {string}  [config.continueKey="Space"]       - KeyCode used in the "press X to continue" hint
 * @param {string|null} [config.continueLabel=null]    - Override the displayed key label (bypasses layout resolution)
 * @param {number}  [config.mainFontSizePx=20]         - Fixed main text size in px
 * @param {number}  [config.continueFontSizePx=10]     - Fixed continue hint size in px
 * @param {number}  [config.minMainFontSizePx=11]      - Minimum main text size used by auto-fit
 * @param {number}  [config.widthPx=790]               - Card width in px
 * @param {string}  [config.maxWidth="100%"]          - CSS max-width for responsive behavior
 * @param {number|null} [config.heightPx=null]         - Fixed card height in px
 * @param {number}  [config.minHeightPx=100]           - Card minimum height in px
 * @param {number}  [config.portraitSizePx=100]        - Portrait square size in px
 * @param {number}  [config.verticalOffsetPx=12]       - Upward offset in px (larger value moves card up)
 * @returns {string} HTML string
 */
export function buildHQMessageHTML(jsPsych, config) {
  const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);

  const {
    portrait = "hq-impressed",
    text = "",
    highlights = [],
    continueKey = "Space",
    continueLabel = null,
    showContinueHint = true,
    mainFontSizePx = 22,
    continueFontSizePx = 16,
    widthPx = 790,
    maxWidth = "60%",
    heightPx = 100,
    minHeightPx = 70,
    portraitSizePx = 70,
  } = config;

  const mainFontSizeCss = `${Number.isFinite(mainFontSizePx) ? mainFontSizePx : 20}px`;
  const continueFontSizeCss = `${Number.isFinite(continueFontSizePx) ? continueFontSizePx : 10}px`;
  const cardHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : "auto";
  const cardMinHeightCss = Number.isFinite(heightPx) ? `${heightPx}px` : `${minHeightPx}px`;
  const portraitRenderSizePx = portraitSizePx + 25;
  const cardMaxWidthCss = maxWidth === "none" ? `${widthPx}px` : `min(${widthPx}px, ${maxWidth})`;
  const mainContainerPaddingBottomCss = showContinueHint ? "32px" : "10px";

  const portraitSrc = escapeHtml(`assets/images/instructions/custom/${portrait}.svg`);
  const renderedText = renderTextWithHighlights(text, highlights, layoutConfig, {
    highlightColor: ORANGE,
  });
  const resolvedContinueKey = continueLabel ?? resolveKeyForLayout(continueKey, layoutConfig);

  return `
    <div class="gear-hq-card" style="
      --hq-card-max-width:${cardMaxWidthCss};
      --hq-card-height:${cardHeightCss};
      --hq-card-min-height:${cardMinHeightCss};
      --hq-portrait-size:${portraitRenderSizePx}px;
      --hq-main-font-size:${mainFontSizeCss};
      --hq-continue-font-size:${continueFontSizeCss};
      --hq-main-container-padding-bottom:${mainContainerPaddingBottomCss};
      background:${CARD_BG};
    ">
      <div class="gear-hq-portrait-shell">
        <img
          src="${portraitSrc}"
          alt="HQ portrait"
          class="gear-hq-portrait-img"
        />
      </div>

      <div
        class="gear-hq-main-container"
        style="flex-direction:column;justify-content:center;gap:4px;"
        data-hq-main-container="true"
      >
        <div
          class="gear-hq-main-text"
          style="line-height:1.3;margin:0;margin-right:-10px;color:${TEXT_COLOR};"
          data-hq-main-text="true"
        >${renderedText}</div>

      ${showContinueHint ? `
      <div class="gear-hq-continue-hint" style="color:${HINT_COLOR};" data-hq-continue-text="true">[press <strong style="font-weight:600;color:${HINT_COLOR};">${escapeHtml(resolvedContinueKey)}</strong> to continue]</div>
      ` : ""}
      </div>
    </div>
  `;
}

/**
 * Injects the HQ message panel into the footer area.
 * Call from on_load after startPhase0().
 * Returns the wrapper element for optional external cleanup.
 *
 * @param {JsPsych} jsPsych - jsPsych instance used to resolve layout-dependent labels
 * @param {Object} config - Same config as buildHQMessageHTML
 * @returns {HTMLElement|null}
 */
export function injectHQMessage(jsPsych, config) {
  const verticalOffsetPx = Number.isFinite(config?.verticalOffsetPx)
    ? config.verticalOffsetPx
    : -6;

  const wrapper = mountHQPanel({
    panelId: "hq-message-panel",
    html: buildHQMessageHTML(jsPsych, config),
    verticalOffsetPx,
  });
  if (!wrapper) return null;

  // Fit text after insertion so measurements reflect final layout.
  window.requestAnimationFrame(() => {
    fitHQMessageText(wrapper, {
      minMainFontSizePx: config?.minMainFontSizePx,
    });
  });

  return wrapper;
}

/**
 * Removes the injected HQ message panel from the DOM.
 * Call from on_finish.
 */
export function removeHQMessage() {
  removeHQPanel("hq-message-panel");
}
